import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import GameCard from '../../components/GameCard/GameCard';
import AdBanner from '../../components/ADS/AdBanner';
import { fetchGamezopGames } from '../../services/gameService';
import { slugify } from '../../utils/constants';

const fmt = (n) => {
  if (!n) return '0';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(n);
};

function BlogPreview() {
  const { name } = useParams();
  const [state, setState] = useState(null);
  const [gamesList, setGamesList] = useState([]);

  useEffect(() => {
    fetchGamezopGames().then((games) => {
      setGamesList(games);
      setState(games.find((g) => slugify(g?.name?.en) === name) || null);
    }).catch(() => {});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [name]);

  const share = () => {
    if (navigator.share) navigator.share({ title: state?.name?.en, url: window.location.href });
    else navigator.clipboard.writeText(window.location.href);
  };

  const related = gamesList.filter((g) => g?.name?.en && slugify(g.name.en) !== name).slice(0, 12);

  return (
    <div className="min-h-screen bg-theme">
      {state && (
        <Helmet>
          <title>{`${state.name.en} —Car Games - Play Free Driving and Racing Games Online |Car Games - Free Driving and Racing Games Online Blog`}</title>
          <meta name="description" content={`Read about ${state.name.en} onCar Games - Play Free Driving and Racing Games Online |Car Games - Free Driving and Racing Games Online Blog. ${state?.description?.en?.slice(0, 120) || ''}`} />
          <meta name="robots" content="index, follow" />
          <meta property="og:title" content={`${state.name.en} —Car Games - Play Free Driving and Racing Games Online |Car Games - Free Driving and Racing Games Online Blog`} />
          <meta property="og:url" content={`https://cargames.site/blog/${name}`} />
          <meta property="og:image" content={state?.assets?.cover || state?.assets?.square || 'https://cargames.site/logo.png'} />
          <meta property="og:type" content="article" />
          <meta name="twitter:card" content="summary_large_image" />
          <link rel="canonical" href={`https://cargames.site/blog/${name}`} />
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org', '@type': 'BlogPosting',
            headline: state.name.en,
            description: state?.description?.en || '',
            image: state?.assets?.cover || 'https://cargames.site/logo.png',
            author: { '@type': 'Organization', name: 'Car Games' },
            publisher: { '@type': 'Organization', name: 'Car Games', logo: { '@type': 'ImageObject', url: 'https://cargames.site/logo.png' } },
            mainEntityOfPage: { '@type': 'WebPage', '@id': `https://cargames.site/blog/${name}` },
          })}</script>
        </Helmet>
      )}

      <div className="max-w-4xl mx-auto px-4 py-5 pb-12">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs mb-6 text-dim flex-wrap">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <span>›</span>
          <Link to="/blog" className="breadcrumb-link">Blog</Link>
          <span>›</span>
          <span className="text-main font-semibold">{state?.name?.en || name?.replace(/-/g, ' ')}</span>
        </div>

        {/* Hero Cover */}
        <div className="rounded-2xl overflow-hidden mb-6 relative bg-card-theme" style={{ aspectRatio: '16/7' }}>
          {state?.assets?.cover ? (
            <>
              <img src={state.assets.cover} alt={state?.name?.en} className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)' }} />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="flex flex-wrap gap-2 mb-3">
                  {state?.categories?.en?.map((cat, i) => (
                    <Link key={i} to={`/category/${cat.toLowerCase().replace(/\s+/g, '-')}`} className="cat-chip text-white" style={{ background: 'rgba(79,142,247,0.85)' }}>
                      {cat}
                    </Link>
                  ))}
                </div>
                <h1 className="font-black text-white m-0 font-sora leading-tight" style={{ fontSize: 'clamp(18px,4vw,28px)', textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}>
                  {state?.name?.en}
                </h1>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a1040, #0a1628)' }}>
              <span className="text-6xl">🎮</span>
            </div>
          )}
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Car Games" className="w-7 h-7 rounded-lg" />
              <div>
                <div className="text-xs font-extrabold text-main">Car Games</div>
                <div className="text-[11px] text-dim">Gaming Blog</div>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="text-center">
                <div className="text-sm font-extrabold" style={{ color: '#f59e0b' }}>⭐ {state?.rating || 'N/A'}</div>
                <div className="text-[10px] text-dim font-semibold">Rating</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-extrabold text-game-blue">{fmt(state?.gamePlays)}</div>
                <div className="text-[10px] text-dim font-semibold">Plays</div>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={share} className="btn-ghost text-xs">🔗 Share</button>
            <Link to={`/game/${name}`} className="btn-accent px-4 py-2 text-xs">▶ Play Now</Link>
          </div>
        </div>

        <AdBanner style={{ marginBottom: 28 }} />

        {/* About */}
        <div className="section-card mb-6">
          <h2 className="font-extrabold text-lg text-main font-sora mb-3.5 h-blue">🎮 About {state?.name?.en}</h2>
          <p className="text-sm text-sub leading-loose mb-3">
            {state?.description?.en || `${state?.name?.en} is an exciting free browser game. No download needed — play instantly on any device!`}
          </p>
          <p className="text-sm text-sub leading-loose mb-3">
            Experience the thrill of <strong className="text-main">{state?.name?.en}</strong> with its captivating challenges and dynamic gameplay.
          </p>
          <p className="text-sm text-sub leading-loose">
            Whether you're a casual gamer or dedicated player, <strong className="text-main">{state?.name?.en}</strong> delivers an experience that's accessible and deeply engaging — perfect for all ages!
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { icon: '🎮', label: 'Plays',    value: fmt(state?.gamePlays), color: '#4f8ef7' },
            { icon: '⭐', label: 'Rating',   value: state?.rating || 'N/A', color: '#f59e0b' },
            { icon: '💻', label: 'Platform', value: 'Browser',              color: '#22c55e' },
            { icon: '📐', label: 'Mode',     value: state?.isPortrait ? 'Portrait' : 'Wide', color: '#a855f7' },
          ].map(({ icon, label, value, color }) => (
            <div key={label} className="card text-center py-3 px-2">
              <div className="text-xl mb-1">{icon}</div>
              <div className="text-base font-extrabold mb-0.5" style={{ color }}>{value}</div>
              <div className="text-[10px] text-dim font-semibold uppercase tracking-wider">{label}</div>
            </div>
          ))}
        </div>

        {/* Key Features */}
        <div className="section-card mb-6">
          <h2 className="font-extrabold text-lg text-main font-sora mb-4 h-purple">✨ Key Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { icon: '🚀', title: 'Instant Play',     desc: 'No downloads. Click and play directly in your browser.' },
              { icon: '🏆', title: 'Challenging Levels', desc: 'Master increasingly difficult stages.' },
              { icon: '📱', title: 'Cross-Platform',   desc: 'Play on desktop, tablet, or mobile.' },
              { icon: '🎯', title: 'Addictive Fun',    desc: 'Engaging mechanics that keep you entertained.' },
            ].map((f, i) => (
              <div key={i} className="flex gap-3 items-start px-3.5 py-3 rounded-xl card-hover border-theme">
                <span className="text-xl flex-shrink-0">{f.icon}</span>
                <div>
                  <div className="text-xs font-extrabold text-main mb-0.5">{f.title}</div>
                  <div className="text-xs text-dim leading-relaxed">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How to Play */}
        <div className="section-card mb-6">
          <h2 className="font-extrabold text-lg text-main font-sora mb-4 h-green">🕹️ How to Play {state?.name?.en}</h2>
          <div className="flex flex-col gap-3">
            {[
              { step: '1', text: 'Click "Play Now" to open the game page and start instantly.' },
              { step: '2', text: 'Use mouse, keyboard, or touch controls to navigate.' },
              { step: '3', text: 'Follow on-screen instructions and complete objectives.' },
              { step: '4', text: 'Challenge yourself for high scores and share with friends!' },
            ].map((s) => (
              <div key={s.step} className="flex gap-3 items-start">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0 font-sora"
                  style={{ background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)' }}>{s.step}</div>
                <p className="text-sm text-sub leading-relaxed m-0 pt-1">{s.text}</p>
              </div>
            ))}
          </div>
        </div>

        <AdBanner style={{ marginBottom: 24 }} />

        {/* Tips */}
        <div className="section-card mb-6">
          <h2 className="font-extrabold text-lg text-main font-sora mb-4 h-yellow">💡 Tips & Strategies</h2>
          <div className="flex flex-col gap-3">
            {[
              { icon: '🎯', tip: 'Master the Basics',      desc: 'Understand core mechanics before advanced strategies.' },
              { icon: '⚡', tip: 'Practice Makes Perfect', desc: 'Regular sessions build muscle memory.' },
              { icon: '🧠', tip: 'Think Ahead',            desc: 'Plan moves strategically rather than relying on reflexes.' },
              { icon: '🔄', tip: 'Learn from Mistakes',    desc: 'Each failure is a learning opportunity.' },
            ].map((t, i) => (
              <div key={i} className="flex gap-3 items-start px-3.5 py-2.5 rounded-xl bg-hover-theme border-l-4 border-yellow-500">
                <span className="text-lg flex-shrink-0">{t.icon}</span>
                <div>
                  <span className="text-xs font-extrabold text-main">{t.tip}: </span>
                  <span className="text-xs text-sub">{t.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tags */}
        {state?.tags?.en?.length > 0 && (
          <div className="section-card mb-6">
            <h2 className="font-extrabold text-base text-main font-sora mb-3">🏷️ Tags</h2>
            <div className="flex flex-wrap gap-2">
              {state.tags.en.map((tag, i) => (
                <Link key={i} to={`/tag/${tag.toLowerCase().replace(/\s+/g, '-')}`} className="tag-chip">#{tag}</Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="rounded-2xl mb-7 relative overflow-hidden border border-purple-600/30"
          style={{ background: 'linear-gradient(135deg, #1a1040, #0a1628)', padding: 'clamp(20px,4vw,28px)' }}>
          <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full pointer-events-none" style={{ background: 'rgba(124,58,237,0.15)', filter: 'blur(30px)' }} />
          <div className="relative z-10">
            <h2 className="font-black text-white font-sora mb-2.5" style={{ fontSize: 'clamp(16px,3vw,22px)' }}>
              🎯 Ready to Play {state?.name?.en}?
            </h2>
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#9aa0b8', maxWidth: 520 }}>
              Jump in now — no downloads, no sign-ups. Play free instantly on any device!
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { bg: '#22c55e', text: '✅ Free to Play' },
                { bg: '#4f8ef7', text: '📱 Mobile Friendly' },
                { bg: '#7c3aed', text: '🚀 No Download' },
                { bg: '#f59e0b', text: '⚡ Instant Play' },
              ].map((b, i) => (
                <span key={i} className="text-white text-xs font-bold px-3.5 py-1 rounded-full" style={{ background: b.bg }}>{b.text}</span>
              ))}
            </div>
            <Link to={`/game/${name}`} className="btn-accent px-7 py-3 text-sm mt-2">▶ Play {state?.name?.en} Now</Link>
          </div>
        </div>

        <AdBanner style={{ marginBottom: 28 }} />

        {/* Related Games */}
        <div>
          <h2 className="font-extrabold text-lg text-main font-sora mb-1.5 flex items-center gap-2">🎮 More Games You'll Love</h2>
          <p className="text-sm text-dim mb-3.5">Discover more exciting games similar to {state?.name?.en}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
            {related.map((_ele, i) => (
              <GameCard key={i} slug={slugify(_ele.name.en)} name={_ele.name.en} banner={_ele?.assets?.square} />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

export default BlogPreview;
